/** Vocea panoului: dictarea și cititul cu voce tare, cu ce are browserul.
 *
 *  DE CE NATIV ȘI NU UN SERVICIU. Un furnizor de transcriere în plus ar fi
 *  însemnat încă o cheie, încă o factură și audio-ul omului pe încă un drum,
 *  pentru ceva ce Chrome-ul de pe biroul lui face deja. Deci: `SpeechRecognition`
 *  pentru ce spune omul, `speechSynthesis` pentru ce răspunde asistentul. Unde
 *  nu există, butoanele nu apar — nu se cade pe nimic.
 *
 *  LIMBA E A PROFILULUI. Nu a browserului și nu ghicită din text: omul care are
 *  interfața în română dictează în română, iar eticheta BCP-47 (`ro-RO`) e
 *  aceeași cu cea pe care o dă `i18n`. O recunoaștere pornită pe altă limbă nu
 *  greșește un cuvânt, ci toată propoziția.
 *
 *  DICTAREA TRIMITE SINGURĂ. Cât se vorbește, textul curge în câmp și se vede;
 *  la două secunde de tăcere — sau la apăsarea microfonului — el pleacă la
 *  asistent fără alt buton. Vocea e o conversație, iar într-o conversație nu
 *  apeși nimic ca să ți se răspundă: cine dictează are mâinile pe planșă, nu pe
 *  tastatură. Cine vrea să citească întâi ce s-a auzit scrie cu tastatura.
 *
 *  ÎNCHIDEREA CARE TRIMITE E ALTA DECÂT CEA CARE RENUNȚĂ. `opreste` e gestul
 *  omului — «gata, asta am de spus» —, `renunta` e panoul strâns sau plecat de
 *  pe ecran. Ar fi fost același `stop()` pentru amândouă, dar atunci un panou
 *  închis din greșeală ar fi expediat jumătatea de frază de dinainte.
 */
import { blocuri } from "./scris";

// ---------------------------------------------------------------------------
// Ce împrumutăm din browser
// ---------------------------------------------------------------------------

// Tipurile sunt declarate aici, nu luate din `lib.dom`: `SpeechRecognition` a
// intrat în ea abia după versiunea de TypeScript pe care o compilăm, iar din
// interfața lui ne trebuie șapte câmpuri.
type RezultatVorbit = { readonly 0: { transcript: string }; isFinal: boolean };
type EvenimentVorbit = { results: ArrayLike<RezultatVorbit> };
type EvenimentEroare = { error: string };

type Motor = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: EvenimentVorbit) => void) | null;
  onerror: ((e: EvenimentEroare) => void) | null;
  onend: (() => void) | null;
};

type FabricaMotor = new () => Motor;

function fabrica(): FabricaMotor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as Record<string, FabricaMotor | undefined>;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Are browserul ăsta recunoaștere de voce? */
export const seAude = () => fabrica() !== null;

/** Poate browserul ăsta să citească cu voce tare? */
export const sePoateCiti = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

/** Citește-mi răspunsurile cu voce tare: alegere de APARAT, nu de cont.
 *
 *  Nu e o preferință de proiect, e felul în care lucrezi acum: cu căștile pe
 *  urechi într-un open-space sau singur în birou. Deci stă în `localStorage`,
 *  nu pe profil — iar dacă browserul refuză stocarea, se pierde la reîncărcare
 *  și atât. Se alege în pagina de profil; panoul asistentului doar o citește. */
const CHEIE_CITIRE = "ob.asistent.voce";

export function citireaPornita(): boolean {
  try {
    return localStorage.getItem(CHEIE_CITIRE) === "1";
  } catch {
    return false;
  }
}

export function tineMinteCitirea(pornita: boolean): void {
  try {
    localStorage.setItem(CHEIE_CITIRE, pornita ? "1" : "0");
  } catch {
    // Stocare blocată: alegerea ține cât ține pagina.
  }
}

// ---------------------------------------------------------------------------
// Dictarea
// ---------------------------------------------------------------------------

export type Ascultator = {
  /** Gata de spus: se închide microfonul și ce s-a auzit pleacă (`laDictat`). */
  opreste(): void;
  /** Închide microfonul fără să trimită: panou strâns, închis sau demontat. */
  renunta(): void;
};

export type CereriDeAscultat = {
  /** Eticheta BCP-47 a limbii din profil: `ro-RO`, `en-GB`. */
  limba: string;
  /** Tot ce s-a auzit de la pornire, cu bucata nesigură de la coadă cu tot. */
  laText: (text: string) => void;
  /** Microfonul s-a închis cu ceva auzit în el: ASTA se trimite, o dată. */
  laDictat: (text: string) => void;
  /** Codul erorii, așa cum îl dă browserul: `not-allowed`, `network`… */
  laEroare: (cod: string) => void;
  /** S-a închis microfonul — de mână, din tăcere sau din eroare. */
  laSfarsit: () => void;
};

/** Câtă tăcere după vorbă închide microfonul și trimite.
 *
 *  Două secunde: sub ele cade pauza dintre «mută peretele» și «cu zece
 *  centimetri», peste ele omul a terminat și se uită la ecran.
 */
export const TACERE_MS = 2000;

/** Pornește microfonul. `null` dacă browserul nu știe să asculte.
 *
 *  `continuous` cu `interimResults`: o cerere de-a omului are o frază, dar o
 *  frază are pauze în ea, iar fără rezultate provizorii câmpul ar sta gol până
 *  la capăt și ar părea că nu se întâmplă nimic. Tot din ele se măsoară și
 *  tăcerea: ceasul de `TACERE_MS` se ia de la capăt la fiecare vorbă nouă, deci
 *  el se umple numai când chiar nu mai vine nimic.
 *
 *  TRIMISUL STĂ PE `onend`, nu pe ceasul care a expirat și nu pe clic. Și
 *  tăcerea, și apăsarea microfonului cer motorului `stop()`, iar el mai scoate
 *  o ultimă oară fraza — de data asta sigură — înainte să se închidă. Trimis
 *  mai devreme, mesajul ar fi purtat forma provizorie a ultimelor cuvinte.
 */
export function asculta(cereri: CereriDeAscultat): Ascultator | null {
  const Fabrica = fabrica();
  if (!Fabrica) return null;

  const motor = new Fabrica();
  motor.lang = cereri.limba;
  motor.continuous = true;
  motor.interimResults = true;

  /** Ultima formă a frazei. Ea pleacă la închidere, dacă are ce pleca. */
  let auzit = "";
  /** Nu mai pleacă nimic: s-a renunțat sau a picat microfonul. */
  let mut = false;
  let ceas: ReturnType<typeof setTimeout> | null = null;
  const lasaCeasul = () => {
    if (ceas !== null) { clearTimeout(ceas); ceas = null; }
  };

  motor.onresult = (e) => {
    let text = "";
    for (let i = 0; i < e.results.length; i++) {
      text += e.results[i][0].transcript;
    }
    auzit = text.trim();
    cereri.laText(auzit);
    lasaCeasul();
    // Ceasul se pune doar după ce s-a auzit ceva: un microfon deschis peste
    // care nimeni n-a spus nimic n-are ce trimite, deci stă deschis.
    if (!mut && auzit) ceas = setTimeout(() => { ceas = null; motor.stop(); },
                                         TACERE_MS);
  };
  motor.onerror = (e) => {
    // «N-am auzit nimic» și «am oprit eu» nu sunt eșecuri, sunt sfârșitul
    // obișnuit al unei dictări: o bandă roșie pentru ele ar fi zgomot.
    if (e.error === "no-speech" || e.error === "aborted") return;
    mut = true;
    lasaCeasul();
    cereri.laEroare(e.error);
  };
  motor.onend = () => {
    lasaCeasul();
    if (!mut && auzit) cereri.laDictat(auzit);
    cereri.laSfarsit();
  };

  motor.start();
  return {
    opreste: () => { lasaCeasul(); motor.stop(); },
    renunta: () => { mut = true; lasaCeasul(); motor.abort(); },
  };
}

/** Ce era scris înainte de dictare, plus ce s-a dictat.
 *
 *  Dictarea nu șterge ce a apucat omul să scrie cu mâna: se lipește după el.
 */
export function lipit(scris: string, dictat: string): string {
  const baza = scris.trimEnd();
  if (!baza) return dictat;
  if (!dictat) return baza;
  return `${baza} ${dictat}`;
}

// ---------------------------------------------------------------------------
// Cititul cu voce tare
// ---------------------------------------------------------------------------

/** Răspunsul modelului, așa cum se rostește.
 *
 *  Markdown-ul e pentru ochi: stelele, gardurile și accentele grave n-au cum
 *  să se audă. Blocurile de cod se sar cu totul — un `{"alege": ["u3"]}` citit
 *  cu voce tare e un minut de zgomot din care nu se înțelege nimic.
 */
export function deRostit(text: string): string {
  const bucati: string[] = [];
  for (const b of blocuri(text)) {
    if (b.fel === "cod") continue;
    if (b.fel === "lista") bucati.push(b.randuri.join(". "));
    else bucati.push(b.text);
  }
  // Blocurile se leagă cu punct, ca vocea să se oprească între ele — dar numai
  // unde nu e deja unul: «Am rulat:.» se aude ca o bâlbâială.
  const vorba = bucati.reduce((strans, bucata) => {
    const b = bucata.trim();
    if (!b) return strans;
    if (!strans) return b;
    return /[.,;:!?…]$/.test(strans) ? `${strans} ${b}` : `${strans}. ${b}`;
  }, "");
  return vorba
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\*\*([^*]*)\*\*/g, "$1")
    .replace(/\*([^*\n]*)\*/g, "$1")
    .replace(/\s+/g, " ")
    .replace(/\s+([.,;:!?])/g, "$1")
    .trim();
}

/** Citește textul cu voce tare, în limba interfeței. */
export function citesteTare(text: string, limba: string): void {
  if (!sePoateCiti()) return;
  const vorba = deRostit(text);
  if (!vorba) return;
  // Ce se citea înainte se taie: două răspunsuri suprapuse nu se înțeleg, iar
  // cel vechi oricum nu mai e pe ecran.
  window.speechSynthesis.cancel();
  const rostire = new SpeechSynthesisUtterance(vorba);
  rostire.lang = limba;
  // Vocile se încarcă asincron în Chrome: dacă lista e încă goală, rămâne
  // eticheta de limbă, după care motorul alege singur.
  const scurt = limba.slice(0, 2).toLowerCase();
  const voce = window.speechSynthesis
    .getVoices()
    .find((v) => v.lang?.replace("_", "-").toLowerCase().startsWith(scurt));
  if (voce) rostire.voice = voce;
  window.speechSynthesis.speak(rostire);
}

/** Taci: la o discuție nouă, la un panou închis, la un microfon pornit. */
export function taci(): void {
  if (sePoateCiti()) window.speechSynthesis.cancel();
}
